<script lang="ts">
import {defineComponent} from 'vue'
import {$inj, $injByInterface} from '@/common/decorators/depinject'
import EnrollmentServiceFactory from '@/common/services/Enrollment/enrollmentServiceFactory'
import Base64Factory from '@/common/services/utils/Base64Factory'
import type IWorkspaceStore from '@/common/services/Workspace/IWorkspaceStore'
import {cloneDeep, isEmpty} from 'lodash'
import bltEmptyData from '@/common/components/bltEmptyData/bltEmptyData.vue'
import '@banno/jha-wc/src/icons/jha-icon-credit-card.js'

export default defineComponent({
  name: 'bltProductImages',
  emits: ['closeCardImageModal', 'saveCardImageModal'],
  components: {
    bltEmptyData
  },
  props: {
    products: {
      type: Object
    },
    showChooseCardImage: {
      type: Boolean
    },
    modalClass: {
      type: String
    }
  },
  data() {
    return {
      workspaceUUID: '' as string,
      enrollmentId: 0 as number,
      selectedImage: {} as any,
      bltProduct: {} as any,
      isFullScreenImage: false as boolean,
      isEnableSaveCardImage: true as boolean,
      isCardImageLoader: true as boolean,
      emptyData: 'There are no debit card images available for this product.'
    }
  },
  setup() {
    const enrollmentServiceFactory = $inj(EnrollmentServiceFactory)
    const workspaceStore = $injByInterface<IWorkspaceStore>('IWorkspaceStore')
    const base64Factory = $inj(Base64Factory)
    return {
      enrollmentServiceFactory,
      workspaceStore,
      base64Factory
    }
  },
  created() {
    this.workspaceUUID = this.workspaceStore.workspaceUUID
    this.enrollmentId = this.workspaceStore.enrollment.enrollmentId
  },
  methods: {
    saveCardImageModal() {
      delete this.bltProduct.images
      this.bltProduct.imageId = this.selectedImage.imageId
      const selectedImageObj = {
        product: this.bltProduct,
        thumbnail: this.base64Factory.getFromUnencoded(
          this.selectedImage.thumb.imageDataBase64,
          this.selectedImage.thumb.imageFormat
        )
      }
      this.$emit('saveCardImageModal', selectedImageObj)
      this.isFullScreenImage = false
      this.isEnableSaveCardImage = true
      this.selectedImage = {}
      this.bltProduct = {}
    },
    closeCardImageModal() {
      if (this.selectedImage) {
        delete this.selectedImage
      }
      this.$emit('closeCardImageModal')
    },
    loadFullScreen(image: any) {
      this.selectedImage = image
      this.enrollmentServiceFactory
        .getProductImageHQ(this.workspaceUUID, this.enrollmentId, this.bltProduct, image.imageId)
        .then((response: any) => {
          this.isFullScreenImage = true
          this.isEnableSaveCardImage = false
          image.HQImage = response
        })
    },
    onInit() {
      this.isCardImageLoader = true
      this.selectedImage = {}
      this.isFullScreenImage = false
      this.enrollmentServiceFactory
        .getProductImageId(this.workspaceUUID, this.enrollmentId, this.bltProduct.accountServiceId)
        .then((response: any) => {
          this.bltProduct.images = response
          if (!isEmpty(this.bltProduct.images)) {
            return Promise.all(
              this.bltProduct.images.map((image: any) => {
                image.accountServiceCode = this.bltProduct.accountServiceCode
                image.productCode = this.bltProduct.productCode
                return this.enrollmentServiceFactory
                  .getProductThumbnail(
                    this.workspaceUUID,
                    this.enrollmentId,
                    this.bltProduct.accountServiceId,
                    image.imageId
                  )
                  .then((thumb: any) => {
                    this.isCardImageLoader = false
                    image.thumb = thumb
                    return image
                  })
              })
            )
          } else {
            this.isCardImageLoader = false
          }
        })
    },
    innerScreenBackBtn() {
      this.isFullScreenImage = false
      this.isEnableSaveCardImage = true
      this.selectedImage = {}
    }
  },
  watch: {
    products(newVal) {
      this.bltProduct = cloneDeep(newVal)
      if (!isEmpty(this.bltProduct)) this.onInit()
    }
  }
})
</script>

<template>
  <blt-modal-dialog
    :showDialog="showChooseCardImage"
    title="Choose debit card image"
    showConfirm
    confirmLabel="Save"
    @cancel="closeCardImageModal"
    @confirm="saveCardImageModal"
    @backButton="innerScreenBackBtn"
    :innerScreenBackBtn="isFullScreenImage"
    :confirmDisabled="isEnableSaveCardImage"
    :stateClass="modalClass"
    :smallDialog="true"
    type="small-dialog">
    <template #dialog-content>
      <blt-contentloader :contentLoader="isCardImageLoader"></blt-contentloader>
      <div
        class="dialog-content"
        v-if="!isCardImageLoader">
        <div
          class="product-card-block"
          v-if="!bltProduct?.images?.length">
          <bltEmptyData :bltText="emptyData">
            <template v-slot:jhaIcon>
              <jha-icon-credit-card class="secondary-fill-color"></jha-icon-credit-card>
            </template>
          </bltEmptyData>
        </div>
        <div v-if="bltProduct.images && bltProduct.images.length">
          <div v-if="!isFullScreenImage">
            <div class="regular-text mt-24 mb-16">Select a card image:</div>
            <div
              class="card-dp"
              v-if="bltProduct.images && !Object.keys(selectedImage).length">
              <div class="flex-gap">
                <div
                  class="card-images"
                  :class="{'hide-bg': image && image?.thumb?.encodedBase64}"
                  v-for="image in bltProduct.images"
                  :key="image"
                  @click.stop="loadFullScreen(image)">
                  <img
                    :src="image?.thumb?.encodedBase64"
                    alt="debit card image" />
                </div>
              </div>
            </div>
          </div>
          <div v-if="isFullScreenImage">
            <div
              class="selected-card-placeholder"
              v-if="Object.keys(selectedImage).length">
              <img
                class="fullSizeImage"
                :src="selectedImage.thumb?.encodedBase64"
                alt="Selected image (Thumbnail)"
                v-if="!selectedImage.HQImage" />
              <img
                class="fullSizeImage"
                :src="selectedImage.HQImage?.encodedBase64"
                alt="Selected image (Full-size)"
                v-if="selectedImage.HQImage" />
            </div>
          </div>
        </div>
      </div>
    </template>
  </blt-modal-dialog>
</template>

<style scoped>
.dialog-content {
  width: 100%;
  padding: 0px var(--jh-space-600);
  box-sizing: border-box;
}

.card-dp {
  text-align: center;
}

.regular-text {
  font-size: 14px;
  font-weight: 400;
}

.flex-gap {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 48px;
  justify-content: space-between;
}

.card-images {
  border-radius: 3px;
  margin-bottom: 24px;
  cursor: pointer;
}

.card-images img {
  max-height: 120px;
  max-width: 120px;
  height: auto;
  width: auto;
}

.selected-card-placeholder {
  border-radius: 10px;
  margin: 0 auto;
  padding: 24px 0px;
  text-align: center;
}

.fullSizeImage {
  max-height: 400px;
  max-width: 400px;
  height: auto;
  width: auto;
}

.product-card-block {
  padding-top: var(--jh-space-900);
  padding-bottom: var(--jh-space-900);
  padding-right: var(--jh-space-600);
}

.product-card-detail-block {
  line-height: 20px;
  font-weight: 500;
  display: block;
}

.empty-state {
  text-align: center;
}

.hide-bg {
  background: transparent;
}

@media only screen and (max-width: 599px) {
  .dialog-content {
    padding: 0px var(--jh-space-400);
  }
  .flex-gap {
    gap: inherit;
    justify-content: space-between;
  }
}
</style>
